// PC137: Impersonation banner + exit control.
// Shows across the top of the app whenever localStorage has
// `original_access_token` set (meaning the current session is an
// impersonated one). Clicking Exit calls the backend end endpoint,
// restores the original JWT, and reloads to /admin/platform-overview.

import React, { useEffect, useState, useCallback } from 'react';
import { LogOut, UserCheck } from 'lucide-react';
import apiClient from '../api';

const ImpersonationBanner = () => {
  const [active, setActive] = useState(false);
  const [target, setTarget] = useState(null);
  const [exiting, setExiting] = useState(false);

  const detect = useCallback(() => {
    const stashed = localStorage.getItem('original_access_token');
    setActive(!!stashed);
    if (stashed) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        setTarget(u);
      } catch (_) {
        setTarget(null);
      }
    }
  }, []);

  useEffect(() => {
    detect();
    // Re-check on tab focus in case another tab exited impersonation
    window.addEventListener('focus', detect);
    return () => window.removeEventListener('focus', detect);
  }, [detect]);

  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      // Best-effort — tell backend we're ending so it can log
      try {
        await apiClient.post('/admin/impersonate/end');
      } catch (_) {}

      // Restore original admin session
      const orig = localStorage.getItem('original_access_token');
      const origUser = localStorage.getItem('original_user');
      if (orig) {
        localStorage.setItem('access_token', orig);
        localStorage.removeItem('original_access_token');
      }
      if (origUser) {
        localStorage.setItem('user', origUser);
        localStorage.removeItem('original_user');
      }
      // Clear jurisdiction cache from impersonated session
      localStorage.removeItem('jurisdiction_id');

      // Hard-reload to /admin/platform-overview so AuthContext re-hydrates
      window.location.href = '/admin/platform-overview';
    } finally {
      setExiting(false);
    }
  };

  if (!active) return null;

  return (
    <div className="w-full bg-yellow-400 text-slate-900 px-4 py-2 shadow-md sticky top-0 z-50 flex items-center justify-between text-sm font-medium">
      <div className="flex items-center gap-2">
        <UserCheck className="w-4 h-4" />
        <span>
          You are impersonating{' '}
          <strong>
            {target?.first_name || ''} {target?.last_name || ''}{' '}
            {target?.email ? `(${target.email})` : ''}
          </strong>
          {target?.user_role ? ` — ${target.user_role}` : ''}. All actions are audit-logged to your admin identity.
        </span>
      </div>
      <button
        onClick={handleExit}
        disabled={exiting}
        className="flex items-center gap-1.5 bg-slate-900 text-yellow-300 hover:bg-slate-800 px-3 py-1 rounded font-semibold"
      >
        <LogOut className="w-4 h-4" />
        {exiting ? 'Exiting…' : 'Exit impersonation'}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
